import { useState, useEffect } from 'react';
import { sanitizeHtml } from '../../../utils/htmlSanitizer';
import { fetchBody } from '../services/MessagesService';
import { Message } from '../types';

/**
 * FNP-1532: the body comes from the API, not from S3.
 *
 * This used to call Amplify `downloadData` with the signed-in user's Cognito
 * credentials, against a path assembled here from the message's S3 pointers.
 * Two things were wrong with that, and the second one broke production:
 *
 *  1. The `private/{identityId}/` prefix was an Amplify Storage convention, never
 *     an authorization boundary (FNP-877). Authorization now lives in the API,
 *     which checks alias ownership before reading anything.
 *  2. After the EMAIL OU split the bucket moved to another account, so the
 *     browser's credentials no longer reach it at all — every message rendered
 *     "Failed to load message content".
 *
 * ⚠️ The client-side artifact preference that used to live here is GONE, not
 * ported. It preferred `htmlObjectKey` and disagreed with the server's
 * `text -> parsed -> html`, which is the server's domain logic re-implemented in
 * the browser and drifting from it.
 */
export const useMessageContent = (record: Message) => {
    const [messageHtml, setMessageHtml] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        const loadMessage = async () => {
            setLoading(true);
            setError(null);

            if (!record?.id) {
                setMessageHtml('');
                setLoading(false);
                return;
            }

            try {
                const body = await fetchBody(record.id);

                if (cancelled) {
                    return;
                }

                // The server picks the artifact and reports which one via
                // `contentType`. Prefer the HTML it served; fall back to the text
                // artifact wrapped in a <pre> so whitespace survives.
                const html = body?.htmlBody
                    ? sanitizeHtml(body.htmlBody)
                    : body?.textBody
                        ? `<pre>${sanitizeHtml(body.textBody)}</pre>`
                        : '';

                if (!html) {
                    // A message with no readable artifact is a real state (the
                    // outbound path only writes the bodies it was given), not an
                    // error. Say so plainly rather than showing a failure.
                    setMessageHtml('<p>This message has no content.</p>');
                    setLoading(false);
                    return;
                }

                setMessageHtml(
                    body?.truncated
                        ? `${html}<p><em>This message was too large to display in full.</em></p>`
                        : html
                );
            } catch (err) {
                if (cancelled) {
                    return;
                }
                console.error('Failed to load message:', err);
                setError('Failed to load message content');
                setMessageHtml('<p>Failed to load message content</p>');
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadMessage();

        return () => {
            cancelled = true;
        };
    }, [record?.id]);

    return { messageHtml, loading, error };
};
